/**
 * AliasesSection.tsx — the "Aliases remembered" pane inside Settings (Task
 * 3, spec 2026-09-25 "aliases remembered pane"). Lists every remembered
 * phone→contact pin (`contacts_alias`, Tasks 1/2) beside what Halo alone
 * would resolve that number to RIGHT NOW, flags the rows where the two
 * disagree, and lets an admin remove a bad pin.
 *
 * MIRRORS LinesSection/UsersSection'S OWN CONVENTIONS — admin-guard before
 * ever attempting the fetch, `useStore` for `admin`/`pushToast`, a
 * load-error state with the server's own honest message.
 *
 * GET /ops/aliases IS ADMIN-GATED SERVER-SIDE (services/ops/src/routes/
 * aliases.ts's own header comment — unlike `GET /ops/lines`, which any
 * signed-in staff actor may read). So this pane's self-gate is not a
 * courtesy shortcut the way `LinesSection`'s is: it is simply stating the
 * real refusal that route would give a non-admin, before ever making the
 * request that would otherwise say it — same discipline `UsersSection`'s
 * own header comment describes for `GET /ops/users`.
 *
 * MISMATCH ROWS SORT TO THE TOP. That is the whole point of this pane — the
 * D&G case made visible — so a row Halo now disagrees with must never be
 * buried below a page of rows that still match. `Array.prototype.sort` is
 * stable, so two rows that already agree on `mismatch` keep the server's
 * own order (alphabetical by handle) relative to each other.
 *
 * TWO-CLICK CONFIRM, SAME SHAPE AS UserSessions'S "sign out everywhere" —
 * removing a pin changes call routing, so the first click only arms the
 * row (no client call at all) and swaps the button's own text for the real
 * consequence; a second click on the SAME row is what actually removes it.
 * Only one row is ever armed at a time — arming a different row's Remove
 * silently disarms whichever one was armed before, the same "there is only
 * one confirm live at a time" shape a plain click-away-to-cancel affords.
 */
import { useEffect, useState } from 'react'
import { ops } from '../../lib/ops'
import type { AliasLive, AliasRow } from '../../lib/ops'
import { useStore } from '../../store'
import { Button, Card, Chip } from '../ui'
import { formatPhoneNumber } from '../context/format'

/**
 * README §7b-style fixed grid, same discipline as `LINES_ROW_GRID`'s own
 * comment on why a fixed pixel/minmax grid — not intrinsic sizing — is what
 * keeps a table's columns lined up. Four columns: Number, Pinned to, Halo
 * says now, Remove. Stacks to one column below `lg`, same as Lines/Users.
 */
export const ALIASES_ROW_GRID =
  'grid grid-cols-1 gap-3 px-4 py-3 lg:grid-cols-[170px_minmax(180px,1fr)_minmax(180px,1fr)_260px] lg:items-center'

/**
 * The "Pinned to" cell. Falls back to the raw contact id when `pinnedName`
 * is null (a pin can exist before that contact's name was ever fetched),
 * and drops the " · client" tail entirely rather than showing " · null"
 * when there is no client on record for it.
 */
function pinnedToLabel(row: AliasRow): string {
  const name = row.pinnedName ?? row.pinnedContactId
  return row.pinnedClientName ? `${name} · ${row.pinnedClientName}` : name
}

/**
 * The "Halo says now" cell — task-3-brief.md's exact per-kind wording.
 * `match` falls back to the contact id the same way `pinnedToLabel` does,
 * for the same reason: never render the literal "null" in a cell a person
 * reads as a name.
 */
export function haloSaysNowLabel(live: AliasLive): string {
  switch (live.kind) {
    case 'match': {
      const name = live.name ?? live.contactId
      return live.clientName ? `${name} · ${live.clientName}` : name
    }
    case 'ambiguous':
      return `${live.count} people at ${live.clientName ?? 'multiple clients'}`
    case 'none':
      return 'No Halo match'
    case 'unavailable':
      return "Couldn't check"
  }
}

/** Mismatch rows first — see this file's own header comment on why, and on why this never reorders two rows that already agree. */
function sortedAliases(rows: AliasRow[]): AliasRow[] {
  return [...rows].sort((a, b) => Number(b.mismatch) - Number(a.mismatch))
}

export function AliasesSection() {
  const admin = useStore((s) => s.admin)
  const pushToast = useStore((s) => s.pushToast)

  const [aliases, setAliases] = useState<AliasRow[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  // Only one row may be armed at a time — see this file's own header
  // comment on the two-click confirm.
  const [armedHandle, setArmedHandle] = useState<string | null>(null)

  useEffect(() => {
    // Same reason aliases.ts's own header comment gives for gating GET
    // itself: this never even fires the request for a non-admin.
    if (!admin) return
    let cancelled = false
    setAliases(null)
    setLoadError(null)
    setArmedHandle(null)
    ops
      .aliases()
      .then((rows) => {
        if (!cancelled) setAliases(rows)
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'Could not load remembered aliases.')
      })
    return () => {
      cancelled = true
    }
  }, [admin])

  async function remove(row: AliasRow) {
    if (armedHandle !== row.handle) {
      setArmedHandle(row.handle)
      return
    }
    setArmedHandle(null)
    try {
      await ops.removeAlias(row.handle)
      setAliases((prev) => (prev ? prev.filter((a) => a.handle !== row.handle) : prev))
      pushToast({ text: `Stopped remembering ${formatPhoneNumber(row.handle) ?? row.handle}.`, tone: 'ok' })
    } catch (err) {
      pushToast({
        text: err instanceof Error ? err.message : `Could not remove the alias for ${row.handle}.`,
        tone: 'bad',
      })
    }
  }

  if (!admin) {
    // The real refusal `GET /ops/aliases` would give a non-admin
    // (aliases.ts's `not-admin`), said before the request that would
    // otherwise say it — same discipline UsersSection's own non-admin
    // branch uses for `GET /ops/users`.
    return (
      <p className="text-[13px] text-mid" data-testid="aliases-admin-required">
        Reading remembered aliases requires an admin. Ask an admin to grant you admin from this pane.
      </p>
    )
  }

  return (
    <div data-testid="aliases-section" className="flex flex-col gap-4">
      {loadError ? (
        <p className="text-[13px] text-bad" data-testid="aliases-load-error">
          {loadError}
        </p>
      ) : aliases === null ? (
        <p className="text-[13px] text-mid" data-testid="aliases-loading">
          Loading…
        </p>
      ) : aliases.length === 0 ? (
        <p className="text-[13px] text-mid" data-testid="aliases-empty">
          No remembered aliases yet.
        </p>
      ) : (
        <Card className="overflow-x-auto divide-y divide-subtle">
          <div
            data-testid="aliases-header-row"
            className={[
              'hidden bg-sunken text-[10.5px] font-semibold uppercase tracking-[.08em] text-lo lg:grid',
              ALIASES_ROW_GRID,
            ].join(' ')}
          >
            <span>Number</span>
            <span>Pinned to</span>
            <span>Halo says now</span>
            <span>&nbsp;</span>
          </div>
          {sortedAliases(aliases).map((row) => {
            const armed = armedHandle === row.handle
            const numberLabel = formatPhoneNumber(row.handle) ?? row.handle
            return (
              <div key={row.handle} data-testid={`alias-row-${row.handle}`} className={ALIASES_ROW_GRID}>
                <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                  {row.mismatch ? <Chip tone="warn">Halo disagrees</Chip> : null}
                  <span
                    className="min-w-0 truncate whitespace-nowrap text-[13px] text-hi"
                    data-testid={`alias-row-${row.handle}-number`}
                  >
                    {numberLabel}
                  </span>
                </div>
                <span className="min-w-0 text-[13px] text-hi" data-testid={`alias-row-${row.handle}-pinned`}>
                  {pinnedToLabel(row)}
                </span>
                <span className="min-w-0 text-[13px] text-mid" data-testid={`alias-row-${row.handle}-halo`}>
                  {haloSaysNowLabel(row.live)}
                </span>
                <div>
                  <Button
                    type="button"
                    variant="danger"
                    className="whitespace-nowrap"
                    data-testid={`alias-row-${row.handle}-remove`}
                    onClick={() => void remove(row)}
                  >
                    {armed
                      ? `Stop remembering ${numberLabel} as ${row.pinnedName ?? row.pinnedContactId}? New calls will be identified from Halo again.`
                      : 'Remove'}
                  </Button>
                </div>
              </div>
            )
          })}
        </Card>
      )}
    </div>
  )
}
